import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";

import { map, Observable, take } from "rxjs";

import { HttpBaseService } from "../http/http-base.service";
import {
  CreateSubNetworkPayload,
  NetWorkDetail,
  NetWorkFilter,
  NetWorkList,
  NetworkDetailResponse
} from "../../models/core/network.interface";
import { ManagerListFilters, ManagerListItem } from "../../models/core/manager.interface";
import { DEFAULT_PAGINATION, Pagination } from "../../models/pagination.interface";
import { PaginatedResponse } from "../../models/paginated-response.interface";
import { buildManagerListParams } from "../../utils/manager-list-params.util";
import { sharedEnvironment } from "../../../environments/shared-environment";

const FILTER_PAGINATION: Pagination = { pageNumber: 1, pageSize: 100 };

@Injectable({
  providedIn: "root"
})
export class NetworkService extends HttpBaseService {
  protected override getApiEndpoint(): string[] {
    return [sharedEnvironment.serviceUrl, "api", "core/network"];
  }

  public getNetworkList(
    name = "",
    idNetworkReference: string,
    sorts: string[] = [],
    pagination: Pagination = DEFAULT_PAGINATION
  ): Observable<PaginatedResponse<NetWorkList>> {
    let params = new HttpParams()
      .set("name", name)
      .set("filters", "")
      .set("sorts", sorts.join(","))
      .set("page", pagination.pageNumber.toString())
      .set("pageSize", pagination.pageSize.toString());

    if (idNetworkReference) {
      params = params.set("idNetworkReference", idNetworkReference);
    }

    return this.http
      .get<PaginatedResponse<NetWorkList>>(this.buildPath(), { params })
      .pipe(take(1));
  }

  /**
   * Dados essenciais da rede (ou regional) para o resolver de rota: nome e id da rede-mãe.
   *
   * Usa `GET /network/{id}/detail`, e NÃO `GET /network/{id}`: a segunda rota não existe — nem no
   * gateway (`ocelot.core-api.json`) nem na API (`GetNetworkEndpoint` é `Get("")`). Em F5 o resolver
   * caía em 404 e redirecionava para a lista, matando a trilha e o título do detalhe.
   *
   * `txNetworkName` chega com B-1 (DEV-885). Sem o backend deployado, `name` vem vazio e o
   * breadcrumb omite o nível de entidade em vez de mostrar `{network.name}`.
   */
  public getNetworkEssentials(idNetwork: string): Observable<NetWorkDetail> {
    return this.http.get<NetworkDetailResponse>(this.buildPath([idNetwork, "detail"])).pipe(
      take(1),
      map((detail) => ({
        idNetwork,
        name: detail.txNetworkName ?? "",
        idNetworkReference: detail.idNetworkReference,
        parentName: detail.txParentNetworkName
      }))
    );
  }

  public getNetworkDetail(idNetwork: string): Observable<NetWorkDetail> {
    return this.http.get<NetWorkDetail>(this.buildPath([idNetwork, "detail"])).pipe(take(1));
  }

  public getNetworkDetailData(idNetwork: string): Observable<NetworkDetailResponse> {
    return this.http
      .get<NetworkDetailResponse>(this.buildPath([idNetwork, "detail"]))
      .pipe(take(1));
  }

  /**
   * Gestores de uma REDE ou de uma REGIONAL, paginados (DEV-916, CA 08). Uma rota só para os dois
   * níveis: o backend decide por `Network.IdNetworkReference`, do mesmo jeito que
   * `GET {id}/detail` já faz — no domínio uma Regional é uma Rede.
   *
   * Diferente de `getNetworkDetailData`, que devolve os gestores em array nu dentro do payload do
   * detalhe: aqui vêm com envelope, busca, ordenação e paginação no servidor.
   */
  public getNetworkManagers(
    idNetwork: string,
    pagination: Pagination = DEFAULT_PAGINATION,
    filters: ManagerListFilters = {}
  ): Observable<PaginatedResponse<ManagerListItem>> {
    return this.http
      .get<PaginatedResponse<ManagerListItem>>(this.buildPath([idNetwork, "manager"]), {
        params: buildManagerListParams(pagination, filters)
      })
      .pipe(take(1));
  }

  public getFilterNetworkList(
    filters = "",
    sorts = "",
    pagination: Pagination = FILTER_PAGINATION
  ): Observable<NetWorkFilter[]> {
    const params: Record<string, string> = {
      filters,
      sorts,
      pageNumber: pagination.pageNumber.toString(),
      pageSize: pagination.pageSize.toString()
    };

    return this.http.get<NetWorkFilter[]>(this.buildPath(["filter"]), { params }).pipe(take(1));
  }

  public getFilterSubNetworkList(
    idNetworkReference: string,
    filters = "",
    sorts = "",
    pagination: Pagination = FILTER_PAGINATION
  ): Observable<NetWorkFilter[]> {
    const params: Record<string, string> = {
      filters,
      sorts,
      pageNumber: pagination.pageNumber.toString(),
      pageSize: pagination.pageSize.toString()
    };

    if (idNetworkReference) {
      params["IdNetworkReference"] = idNetworkReference;
    }

    return this.http.get<NetWorkFilter[]>(this.buildPath(["filter"]), { params }).pipe(take(1));
  }

  public createSubNetwork(payload: CreateSubNetworkPayload): Observable<void> {
    return this.http.post<void>(this.buildPath(), payload).pipe(take(1));
  }
}
